// CRM Contacts — schema-driven table with filter engine integration.
import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import API_BASE from '../../config';
import toast from 'react-hot-toast';
import CrmTable from '../../components/CrmTable';
import { CONTACT_COLUMNS, ALL_CONTACT_COLUMNS } from '../../components/crmColumns';
import FilterRail from '../../components/FilterRail';
import { CONTACT_SCHEMA } from '../../components/filterSchema';
import CrmAddContact from './CrmAddContact';
import BulkRunModal from '../../components/BulkRunModal';

const EMAIL_DESTINATIONS = [
  { value: 'pipedrive_csv', label: 'Pipedrive (CSV)' },
  { value: 'hubspot_csv', label: 'HubSpot (CSV)' },
  { value: 'bigin_csv', label: 'Zoho Bigin (CSV)' },
  { value: 'zoho_crm_csv', label: 'Zoho CRM (CSV)' },
  { value: 'salesforce_csv', label: 'Salesforce (CSV)' },
];

export default function CrmContacts() {
  const { clientId, client } = useOutletContext();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState(() => CONTACT_COLUMNS.map(c => c.key));
  const [draftKeys, setDraftKeys] = useState(() => CONTACT_COLUMNS.map(c => c.key));
  const [dragKey, setDragKey] = useState(null);

  const [filterTree, setFilterTree] = useState(null);
  const [sfRows, setSfRows] = useState(null);
  const [sfTotal, setSfTotal] = useState(0);
  const [sfLoading, setSfLoading] = useState(false);
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [targets, setTargets] = useState({});
  const [distincts, setDistincts] = useState({});
  const [contactTypes, setContactTypes] = useState([]);
  const [typeMgrOpen, setTypeMgrOpen] = useState(false);
  const [bulkTypeId, setBulkTypeId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [ctypeAction, setCtypeAction] = useState('add');
  const [batchChannel, setBatchChannel] = useState('linkedin');
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [newBatch, setNewBatch] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchDest, setNewBatchDest] = useState('pipedrive_csv');
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [seqData, setSeqData] = useState({ seqs: [], chans: [] });
  const [deleteText, setDeleteText] = useState('');

  useEffect(() => {
    if (showColumns) setDraftKeys(visibleKeys);
  }, [showColumns]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterActive = !!(filterTree && Array.isArray(filterTree.and) && filterTree.and.length > 0);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/contacts?limit=50000`);
      if (!r.ok) throw new Error(`Failed to load contacts (${r.status})`);
      const data = await r.json();
      setContacts(Array.isArray(data) ? data : (data.contacts || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const loadTypes = useCallback(async () => {
    if (!clientId) return;
    try {
      const tok = await getToken();
      const r = await fetch(`${API_BASE}/clients/${clientId}/contact-types`, { headers: { Authorization: `Bearer ${tok}` } });
      if (r.ok) setContactTypes(await r.json());
    } catch (e) { /* non-fatal */ }
  }, [clientId, getToken]);
  useEffect(() => { loadTypes(); }, [loadTypes]);

  const createType = useCallback(async (name, colour) => {
    const tok = await getToken();
    const r = await fetch(`${API_BASE}/clients/${clientId}/contact-types`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ name, colour }),
    });
    if (r.ok) {
      await loadTypes();
      try {
        const token = await getToken();
        const dr = await fetch(`${API_BASE}/filter/contacts/distinct/contact_type?client_id=${parseInt(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (dr.ok) { const d = await dr.json(); setDistincts(s => ({ ...s, contact_type: d })); }
      } catch (_) {}
    }
  }, [clientId, loadTypes, getToken]);

  const deleteType = useCallback(async (id) => {
    await fetch(`${API_BASE}/contact-types/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${await getToken()}` } });
    await loadTypes(); await load();
    try {
      const token = await getToken();
      const dr = await fetch(`${API_BASE}/filter/contacts/distinct/contact_type?client_id=${parseInt(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (dr.ok) { const d = await dr.json(); setDistincts(s => ({ ...s, contact_type: d })); }
    } catch (_) {}
  }, [loadTypes, load, clientId, getToken]);

  const toggleAssign = useCallback(async (contactId, typeId, isOn) => {
    const tok = await getToken();
    if (isOn) {
      await fetch(`${API_BASE}/contacts/${contactId}/types/${typeId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${tok}` } });
    } else {
      await fetch(`${API_BASE}/contacts/${contactId}/types`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ type_id: typeId }),
      });
    }
    await load();
  }, [load, getToken]);

  const saveCell = useCallback(async (contactId, field, value) => {
    await fetch(`${API_BASE}/contacts/${contactId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    });
    await load();
  }, [load]);


  const runServerFilter = useCallback(async (tree, pg, size) => {
    if (!tree || !Array.isArray(tree.and) || tree.and.length === 0) {
      setSfRows(null); setSfTotal(0); return;
    }
    setSfLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/filter/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          client_id: parseInt(clientId),
          tree,
          sort,
          limit: size,
          offset: pg * size,
        }),
      });
      const data = await res.json();
      if (res.ok) { setSfRows(data.rows || []); setSfTotal(data.total || 0); }
      else { setSfRows([]); setSfTotal(0); }
    } catch (e) {
      setSfRows([]); setSfTotal(0);
    } finally {
      setSfLoading(false);
    }
  }, [clientId, getToken, sort]);

  useEffect(() => { runServerFilter(filterTree, page, pageSize); }, [filterTree, page, pageSize, runServerFilter]);
  useEffect(() => { setPage(0); }, [query, pageSize, filterTree]);

  const loadSavedViews = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/saved-filters/contacts?client_id=${parseInt(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setSavedViews(await r.json());
    } catch (_) {}
  }, [clientId, getToken]);

  const loadTargets = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/filter/contacts/targets/campaign?client_id=${parseInt(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setTargets({ campaign: await r.json() });
    } catch (_) {}
  }, [clientId, getToken]);

  const loadDistincts = useCallback(async () => {
    const fields = (CONTACT_SCHEMA.fields || []).filter(f => f.dropdown).map(f => f.dropdown);
    const uniq = [...new Set(fields)];
    const out = {};
    await Promise.all(uniq.map(async df => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/filter/contacts/distinct/${encodeURIComponent(df)}?client_id=${parseInt(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) out[df] = await r.json();
      } catch (_) {}
    }));
    setDistincts(out);
  }, [clientId, getToken]);

  useEffect(() => { if (clientId) { loadSavedViews(); loadTargets(); loadDistincts(); } }, [clientId, loadSavedViews, loadTargets, loadDistincts]);

  async function saveCurrentView(name, shared) {
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/saved-filters/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId), name, tree: filterTree, shared: !!shared }),
      });
      loadSavedViews();
    } catch (_) {}
  }
  async function deleteView(viewId) {
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/saved-filters/${viewId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (activeViewId === viewId) { setActiveViewId(null); setFilterTree(null); }
      loadSavedViews();
    } catch (_) {}
  }

  const q = query.trim().toLowerCase();
  const searched = q
    ? contacts.filter(c => [c.full_name, c.company_name, c.job_title, c.email].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
    : contacts;
  // map a server sort field to the row property used for client-side sort
  const SORT_ROWKEY = { full_name: 'full_name', 'company.name': 'company_name', job_title: 'job_title', email: 'email', status: 'status', score: 'score', id: 'id', created_at: 'created_at', updated_at: 'updated_at', last_emailed: 'last_sent_at', last_opened: 'last_open_at' };
  const sortKey = SORT_ROWKEY[sort.field] || 'created_at';
  const localFiltered = [...searched].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (sortKey === 'score' || sortKey === 'id') { av = Number(av); bv = Number(bv); return sort.dir === 'asc' ? av - bv : bv - av; }
    if (sortKey === 'created_at' || sortKey === 'updated_at' || sortKey === 'last_sent_at' || sortKey === 'last_open_at') {
      av = new Date(av).getTime(); bv = new Date(bv).getTime();
      return sort.dir === 'asc' ? av - bv : bv - av;
    }
    av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
    return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  const onSort = (field) => {
    setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' });
    setPage(0);
  };

  let totalCount, pageRows, totalPages, safePage;
  if (filterActive) {
    totalCount = sfTotal;
    totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    safePage = Math.min(page, totalPages - 1);
    pageRows = sfRows || [];
  } else {
    totalCount = localFiltered.length;
    totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    safePage = Math.min(page, totalPages - 1);
    pageRows = localFiltered.slice(safePage * pageSize, safePage * pageSize + pageSize);
  }

  const toggle = (rowId) => setSelectedIds(prev => { const n = new Set(prev); n.has(rowId) ? n.delete(rowId) : n.add(rowId); return n; });
  const toggleAll = () => setSelectedIds(prev => {
    const on = pageRows.length > 0 && pageRows.every(r => prev.has(r.id));
    if (on) return new Set();
    return new Set(pageRows.map(r => r.id));
  });

  const selectAllMatching = async () => {
    if (!filterActive) { setSelectedIds(new Set(localFiltered.map(r => r.id))); return; }
    setSelectingAll(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/bulk/resolve-ids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entity: 'contacts', client_id: parseInt(clientId), filter: filterTree }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not select all');
      setSelectedIds(new Set((data.ids || []).map(Number)));
    } catch (e) { toast.error(e.message); }
    setSelectingAll(false);
  };

  const bulkType = async (action) => {
    if (!bulkTypeId) { toast.error('Pick a contact type'); return; }
    if (!selectedIds.size) return;
    setBulkBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/contacts/bulk-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contact_ids: Array.from(selectedIds), type_id: parseInt(bulkTypeId), action }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Update failed');
      const t = contactTypes.find(x => String(x.id) === String(bulkTypeId));
      const n = data.changed || 0;
      const nm = t ? t.name : 'type';
      toast.success(action === 'add'
        ? `Added ${nm} to ${n} contact${n === 1 ? '' : 's'}`
        : `Removed ${nm} from ${n} contact${n === 1 ? '' : 's'}`);
      await load();
      if (filterActive) await runServerFilter(filterTree, page, pageSize);
      setBulkModal(null);
    } catch (e) { toast.error(e.message); }
    setBulkBusy(false);
  };

  const refreshList = async () => {
    await load();
    if (filterActive) await runServerFilter(filterTree, page, pageSize);
  };

  const loadSeqData = async () => {
    const [seqs, chans] = await Promise.all([
      fetch(`${API_BASE}/clients/${clientId}/sequences`).then(r => (r.ok ? r.json() : [])).catch(() => []),
      fetch(`${API_BASE}/clients/${clientId}/sequence-channels`).then(r => (r.ok ? r.json() : [])).catch(() => []),
    ]);
    const d = { seqs: Array.isArray(seqs) ? seqs : [], chans: Array.isArray(chans) ? chans : [] };
    setSeqData(d);
    return d;
  };
  const latestSeq = (d, cid) => d.seqs.find(x => x.contact_id === cid) || null;
  const approvedChan = (d, cid, ch) => d.chans.find(x => x.contact_id === cid && x.channel === ch && x.approval_status === 'approved') || null;
  const eligibleFor = (ch) => Array.from(selectedIds).filter(cid => approvedChan(seqData, cid, ch)).length;

  const buildEnrichItems = () => {
    const byId = new Map([...contacts, ...(sfRows || [])].map(c => [c.id, c]));
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return Array.from(selectedIds).map(cid => {
      const c = byId.get(cid);
      const at = c && c.enriched_at ? new Date(c.enriched_at) : null;
      return {
        id: cid,
        name: c ? c.full_name : `Contact ${cid}`,
        sub: c && c.company_name ? c.company_name : '',
        reason: at ? `enriched ${at.toLocaleDateString('en-GB')}` : 'never enriched',
        due: !at || at.getTime() < cutoff,
      };
    });
  };

  const runEnrichOne = async (cid) => {
    try {
      const res = await fetch(`${API_BASE}/contacts/${cid}/enrich`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (res.status === 402) return { creditsOut: true };
      const data = await res.json().catch(() => ({}));
      return res.ok && data.success ? { ok: true, label: 'Done' } : { ok: false, label: 'Failed' };
    } catch (e) { return { ok: false, label: 'Failed' }; }
  };

  const loadBatches = async (ch) => {
    try {
      const ep = ch === 'email' ? 'email-batches' : 'linkedin-batches';
      const r = await fetch(`${API_BASE}/clients/${clientId}/${ep}`);
      const data = r.ok ? await r.json() : [];
      setBatches(Array.isArray(data) ? data.filter(b => b.status !== 'exported') : []);
    } catch (_) { setBatches([]); }
  };

  const openBatch = async () => {
    setBulkModal('batch'); setBatchId(''); setNewBatch(false); setNewBatchName('');
    const d = await loadSeqData();
    const ids = Array.from(selectedIds);
    const li = ids.filter(cid => approvedChan(d, cid, 'linkedin')).length;
    const em = ids.filter(cid => approvedChan(d, cid, 'email')).length;
    const ch = li >= em ? 'linkedin' : 'email';
    setBatchChannel(ch);
    loadBatches(ch);
  };

  const submitBatch = async () => {
    setBulkBusy(true);
    try {
      const ch = batchChannel;
      const ep = ch === 'email' ? 'email-batches' : 'linkedin-batches';
      const ids = Array.from(selectedIds);
      const eligible = ids.filter(cid => approvedChan(seqData, cid, ch));
      const skipped = ids.length - eligible.length;
      if (!eligible.length) throw new Error(`None of the selected have an approved ${ch} sequence`);
      let bid = batchId;
      if (newBatch) {
        if (!newBatchName.trim()) throw new Error('Batch name required');
        const body = ch === 'email' ? { name: newBatchName.trim(), destination_type: newBatchDest } : { name: newBatchName.trim() };
        const cr = await fetch(`${API_BASE}/clients/${clientId}/${ep}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!cr.ok) throw new Error('Could not create batch');
        bid = (await cr.json()).id;
      }
      if (!bid) throw new Error('Pick a batch or create a new one');
      const targetBatch = bid;
      const tally = { added: 0, failed: 0 };
      for (let i = 0; i < eligible.length; i += 5) {
        const group = eligible.slice(i, i + 5);
        await Promise.all(group.map(async cid => {
          const chRow = approvedChan(seqData, cid, ch);
          try {
            const res = await fetch(`${API_BASE}/${ep}/${targetBatch}/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contact_id: cid, sequence_id: chRow.sequence_id }) });
            if (res.ok) tally.added++; else tally.failed++;
          } catch (e) { tally.failed++; }
        }));
      }
      const parts = [`Added ${tally.added}`];
      if (tally.failed) parts.push(`${tally.failed} failed`);
      if (skipped) parts.push(`${skipped} skipped (no approved ${ch})`);
      toast.success(parts.join(' \u00B7 '));
      setBulkModal(null);
      await refreshList();
    } catch (e) { toast.error(e.message); }
    setBulkBusy(false);
  };

  const openCampaign = async () => {
    setBulkModal('campaign'); setCampaignId('');
    loadSeqData();
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/campaigns`);
      const d = r.ok ? await r.json() : [];
      setCampaigns(Array.isArray(d) ? d : (d.items || []));
    } catch (_) { setCampaigns([]); }
  };

  const submitCampaign = async () => {
    if (!campaignId) { toast.error('Pick a campaign'); return; }
    const withSeq = Array.from(selectedIds).map(cid => latestSeq(seqData, cid)).filter(Boolean);
    const skipped = selectedIds.size - withSeq.length;
    if (!withSeq.length) { toast.error('None of the selected have a sequence yet'); return; }
    setBulkBusy(true);
    const total = withSeq.length;
    const tally = { ok: 0, failed: 0 };
    setBulkProgress({ current: 0, total, status: `Enrolling 0 of ${total}` });
    for (let i = 0; i < withSeq.length; i += 5) {
      const group = withSeq.slice(i, i + 5);
      const token = await getToken();
      await Promise.all(group.map(async sq => {
        try {
          const res = await fetch(`${API_BASE}/sequences/${sq.id}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ campaign_id: parseInt(campaignId) }) });
          if (res.ok) tally.ok++; else tally.failed++;
        } catch (e) { tally.failed++; }
      }));
      setBulkProgress({ current: tally.ok + tally.failed, total, status: `Enrolling ${tally.ok + tally.failed} of ${total}` });
    }
    const parts = [`Enrolled ${tally.ok}`];
    if (tally.failed) parts.push(`${tally.failed} failed`);
    if (skipped) parts.push(`${skipped} skipped (no sequence)`);
    if (tally.failed) toast.error(parts.join(' \u00B7 ')); else toast.success(parts.join(' \u00B7 '));
    setBulkBusy(false);
    setBulkModal(null);
    setBulkProgress(p => (p ? { ...p, status: 'Complete.' } : p));
    setTimeout(() => setBulkProgress(null), 4000);
    await refreshList();
  };

  const submitDelete = async () => {
    if (deleteText.trim() !== String(selectedIds.size)) { toast.error(`Type ${selectedIds.size} to confirm`); return; }
    setBulkBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/contacts/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId), contact_ids: Array.from(selectedIds) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) throw new Error('Only workspace admins can delete contacts');
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      const n = data.deleted || 0;
      toast.success(`Deleted ${n} contact${n === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setBulkModal(null);
      await refreshList();
    } catch (e) { toast.error(e.message); }
    setBulkBusy(false);
  };

  const ukDate = (v) => { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB'); };
  const cellToText = (col, row) => {
    const v = row[col.key];
    switch (col.render) {
      case 'types':
      case 'signals': {
        const arr = Array.isArray(v) ? v : [];
        return arr.map(x => (x && typeof x === 'object') ? (x.name || x.type || '') : x).filter(Boolean).join('; ');
      }
      case 'batches': { const arr = Array.isArray(v) ? v : []; return arr.filter(Boolean).join('; '); }
      case 'seqcount': {
        const tot = col.key === 'email_seq' ? row.email_seq_total : row.linkedin_seq_total;
        const app = col.key === 'email_seq' ? row.email_seq_approved : row.linkedin_seq_approved;
        return (tot != null) ? `${app || 0}/${tot}` : '';
      }
      case 'company': return row.company_name || '';
      case 'optout': return v ? 'Yes' : 'No';
      case 'bool': return v ? 'Yes' : 'No';
      case 'ukdate':
      case 'date': return ukDate(v);
      case 'campaign_pill': return row.campaign_name || '';
      default: return (v == null) ? '' : String(v);
    }
  };
  const csvEscape = (s) => { const t = String(s == null ? '' : s); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
  const exportCsv = async () => {
    const cols = visibleKeys.map(k => ALL_CONTACT_COLUMNS.find(c => c.key === k)).filter(Boolean);
    let rows;
    if (filterActive) {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/filter/contacts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ client_id: parseInt(clientId), tree: filterTree, sort, limit: 50000, offset: 0 }),
        });
        const data = await res.json();
        rows = res.ok ? (data.rows || []) : [];
      } catch (_) { rows = []; }
    } else {
      rows = localFiltered;
    }
    const header = cols.map(c => csvEscape(c.label)).join(',');
    const body = rows.map(r => cols.map(c => csvEscape(cellToText(c, r))).join(',')).join('\n');
    const csv = header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `contacts-${(client && client.name ? client.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() : clientId)}-${stamp}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="crmc-wrap">
      <div className="crmc-head">
        <div>
          <h1 className="crmc-title">Contacts</h1>
          <p className="crmc-sub">{client ? client.name : 'Workspace'}</p>
        </div>
        <div className="crmc-head-right">
          <span className="crmc-count"><b>{totalCount}</b>{filterActive ? ' filtered' : ` of ${contacts.length}`}</span>
        </div>
      </div>

      <div className="crmc-tabs">
        <button
          type="button"
          className={'crmc-tab' + (activeViewId === null ? ' crmc-tab--on' : '')}
          onClick={() => { setActiveViewId(null); setFilterTree(null); setPage(0); }}
        >All contacts</button>
        {savedViews.map(v => (
          <button
            key={v.id}
            type="button"
            className={'crmc-tab' + (activeViewId === v.id ? ' crmc-tab--on' : '')}
            onClick={() => { setActiveViewId(v.id); setFilterTree(v.tree); setPage(0); }}
          >{v.name}</button>
        ))}
        <button type="button" className="crmc-tab crmc-tab--add" title="New view" onClick={() => setShowFilters(true)}>+</button>
      </div>

      <div className="crmc-toolbar">
        <div className="crmc-tb-search">
          <span className="crmc-tb-searchicon">⌕</span>
          <input placeholder="Search contacts" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <div className="crmc-tb-actions">
          <button className={'crmc-tb-btn' + (showColumns ? ' crmc-tb-btn--on' : '')} type="button" onClick={() => setShowColumns(v => !v)}><span className="crmc-tb-ic">▦</span>Columns</button>
          <button className={'crmc-tb-btn' + (typeMgrOpen ? ' crmc-tb-btn--on' : '')} type="button" onClick={() => setTypeMgrOpen(v => !v)}><span className="crmc-tb-ic">◉</span>Types</button>
          <button className={'crmc-tb-btn' + (filterActive ? ' crmc-tb-btn--on' : '')} type="button" onClick={() => setShowFilters(v => !v)}>
            <span className="crmc-tb-ic">⛃</span>Filters{filterActive ? ` · ${filterTree.and.length}` : ''}
          </button>
          <button className="crmc-tb-btn" type="button" title="Click a column header to sort" onClick={() => setSort(prev => ({ ...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc' }))}><span className="crmc-tb-ic">↕</span>Sort {sort.dir === 'asc' ? '↑' : '↓'}</button>
          <button className="crmc-tb-btn" type="button" title="Export current view to CSV" onClick={exportCsv}><span className="crmc-tb-ic">↧</span>Export</button>
          <CrmAddContact clientId={clientId} onCreated={() => load()} />
        </div>
      </div>


      {showColumns && (
        <div className="crmc-colpanel">
          <div className="crmc-colpanel-head">
            <span>Edit columns</span>
            <button type="button" className="crmc-colpanel-reset" onClick={() => setDraftKeys(CONTACT_COLUMNS.map(c => c.key))}>Reset to default</button>
          </div>
          <div className="crmc-colpanel-cols">
            <div className="crmc-colpanel-visible">
              <div className="crmc-colpanel-subhead">Visible — drag to reorder</div>
              {draftKeys.map(k => {
                const col = ALL_CONTACT_COLUMNS.find(c => c.key === k);
                if (!col) return null;
                const locked = col.key === 'id' || col.key === 'name';
                return (
                  <div
                    key={col.key}
                    className={'crmc-colrow' + (locked ? ' crmc-colrow--locked' : '') + (dragKey === col.key ? ' crmc-colrow--dragging' : '')}
                    draggable={!locked}
                    onDragStart={() => !locked && setDragKey(col.key)}
                    onDragEnd={() => setDragKey(null)}
                    onDragOver={e => {
                      e.preventDefault();
                      if (locked || !dragKey || dragKey === col.key) return;
                      setDraftKeys(prev => {
                        const next = prev.filter(x => x !== dragKey);
                        const idx = next.indexOf(col.key);
                        next.splice(idx, 0, dragKey);
                        return next;
                      });
                    }}
                  >
                    <span className="crmc-colgrip">{locked ? '🔒' : '⠿'}</span>
                    <span className="crmc-collabel">{col.label}</span>
                    {!locked && (
                      <button type="button" className="crmc-colremove" title="Hide column"
                        onClick={() => setDraftKeys(prev => prev.filter(x => x !== col.key))}>×</button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="crmc-colpanel-hidden">
              <div className="crmc-colpanel-subhead">Hidden — click to add</div>
              <div className="crmc-colpanel-hiddengrid">
                {ALL_CONTACT_COLUMNS.filter(c => !draftKeys.includes(c.key)).map(col => (
                  <button type="button" key={col.key} className="crmc-coladd"
                    onClick={() => setDraftKeys(prev => [...prev, col.key])}>
                    <span>+</span>{col.label}
                  </button>
                ))}
                {ALL_CONTACT_COLUMNS.filter(c => !draftKeys.includes(c.key)).length === 0 && (
                  <div className="crmc-colpanel-allon">All columns visible</div>
                )}
              </div>
            </div>
          </div>
          <div className="crmc-colpanel-foot">
            <button type="button" className="crmc-colbtn" onClick={() => setShowColumns(false)}>Cancel</button>
            <button type="button" className="crmc-colbtn crmc-colbtn--apply"
              onClick={() => { setVisibleKeys(draftKeys); setShowColumns(false); }}>Apply</button>
          </div>
        </div>
      )}

      {typeMgrOpen && (
        <TypeManager
          types={contactTypes}
          onCreate={createType}
          onDelete={deleteType}
          onClose={() => setTypeMgrOpen(false)}
        />
      )}

      {selectedIds.size > 0 && (
        <div className="crmc-bulk">
          <span className="crmc-bulk-n">{selectedIds.size} selected</span>
          {selectedIds.size < totalCount && (
            <button type="button" className="crmc-bulk-link" onClick={selectAllMatching} disabled={selectingAll}>
              {selectingAll ? 'Selecting\u2026' : `Select all ${totalCount}`}
            </button>
          )}
          <button type="button" className="crmc-bulk-link crmc-bulk-link--muted" onClick={() => setSelectedIds(new Set())}>Clear</button>
          <div className="crmc-bulk-right">
            <button type="button" className="crmc-bulk-btn" onClick={() => setEnrichOpen(true)}>Enrich {selectedIds.size}</button>
            <button type="button" className="crmc-bulk-btn crmc-bulk-btn--ghost" onClick={openBatch}>Add to batch</button>
            <button type="button" className="crmc-bulk-btn crmc-bulk-btn--ghost" onClick={openCampaign}>Push to campaign</button>
            <button type="button" className="crmc-bulk-btn crmc-bulk-btn--ghost" onClick={() => { setCtypeAction('add'); setBulkModal('ctype'); }}>Contact type</button>
            <button type="button" className="crmc-bulk-btn crmc-bulk-btn--danger" onClick={() => { setDeleteText(''); setBulkModal('delete'); }}>Delete {selectedIds.size}</button>
          </div>
        </div>
      )}

      {bulkProgress && (
        <div className="crmsq3-prog">
          <div className="crmsq3-prog-bar"><div className="crmsq3-prog-fill" style={{ width: (bulkProgress.total ? Math.round(bulkProgress.current / bulkProgress.total * 100) : 0) + '%' }} /></div>
          <span className="crmsq3-prog-txt">{bulkProgress.status}</span>
        </div>
      )}

      {bulkModal === 'ctype' && (
        <CrmcModal title="Contact type" busy={bulkBusy} onClose={() => setBulkModal(null)}
          footer={<>
            <button type="button" className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setBulkModal(null)} disabled={bulkBusy}>Cancel</button>
            <button type="button" className="crmsq3-btn" onClick={() => bulkType(ctypeAction)} disabled={bulkBusy}>{bulkBusy ? 'Applying\u2026' : `${ctypeAction === 'add' ? 'Add to' : 'Remove from'} ${selectedIds.size}`}</button>
          </>}>
          <p className="crmsq3-modal-s">{selectedIds.size} contacts selected.</p>
          <div className="crmsq3-modal-sec">
            <span className="crmsq3-modal-lbl">Action</span>
            <div className="crmsq3-seg">
              <button type="button" className={ctypeAction === 'add' ? 'crmsq3-seg-on' : ''} onClick={() => setCtypeAction('add')}>Add</button>
              <button type="button" className={ctypeAction === 'remove' ? 'crmsq3-seg-on' : ''} onClick={() => setCtypeAction('remove')}>Remove</button>
            </div>
          </div>
          <span className="crmsq3-modal-lbl">Type</span>
          <select className="crmsq3-modal-sel" value={bulkTypeId} onChange={e => setBulkTypeId(e.target.value)}>
            <option value="">Choose a type</option>
            {contactTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {contactTypes.length === 0 && <p className="crmsq3-modal-note">No types yet. Create one with the Types button.</p>}
        </CrmcModal>
      )}

      {bulkModal === 'batch' && (
        <CrmcModal title="Add to batch" busy={bulkBusy} onClose={() => setBulkModal(null)}
          footer={<>
            <button type="button" className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setBulkModal(null)} disabled={bulkBusy}>Cancel</button>
            <button type="button" className="crmsq3-btn" onClick={submitBatch} disabled={bulkBusy}>{bulkBusy ? 'Adding\u2026' : 'Add'}</button>
          </>}>
          <p className="crmsq3-modal-s">{selectedIds.size} selected {'\u00B7'} {eligibleFor(batchChannel)} have an approved {batchChannel} sequence</p>
          <div className="crmsq3-modal-sec">
            <span className="crmsq3-modal-lbl">Channel</span>
            <div className="crmsq3-seg">
              <button type="button" className={batchChannel === 'linkedin' ? 'crmsq3-seg-on' : ''} onClick={() => { setBatchChannel('linkedin'); setBatchId(''); loadBatches('linkedin'); }}>LinkedIn ({eligibleFor('linkedin')})</button>
              <button type="button" className={batchChannel === 'email' ? 'crmsq3-seg-on' : ''} onClick={() => { setBatchChannel('email'); setBatchId(''); loadBatches('email'); }}>Email ({eligibleFor('email')})</button>
            </div>
          </div>
          <div className="crmsq3-modal-sec">
            <span className="crmsq3-modal-lbl">Target batch</span>
            {!newBatch ? (<>
              <select className="crmsq3-modal-sel" value={batchId} onChange={e => setBatchId(e.target.value)}>
                <option value="">Choose a batch</option>
                {batches.map(b => <option key={b.id} value={b.id}>{b.name} ({b.contact_count} contacts)</option>)}
              </select>
              <p className="crmsq3-modal-note"><button type="button" className="crmsq3-link" onClick={() => setNewBatch(true)}>Create new batch</button></p>
            </>) : (<>
              <input className="crmsq3-modal-inp" value={newBatchName} onChange={e => setNewBatchName(e.target.value)} placeholder="New batch name" autoFocus />
              {batchChannel === 'email' && (
                <select className="crmsq3-modal-sel" value={newBatchDest} onChange={e => setNewBatchDest(e.target.value)} style={{ marginTop: 8 }}>
                  {EMAIL_DESTINATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              )}
              <p className="crmsq3-modal-note"><button type="button" className="crmsq3-link" onClick={() => setNewBatch(false)}>Pick existing</button></p>
            </>)}
          </div>
          <p className="crmsq3-modal-note">Only contacts with an approved {batchChannel} sequence are added. Others are skipped.</p>
        </CrmcModal>
      )}

      {bulkModal === 'campaign' && (
        <CrmcModal title="Push to campaign" busy={bulkBusy} onClose={() => setBulkModal(null)}
          footer={<>
            <button type="button" className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setBulkModal(null)} disabled={bulkBusy}>Cancel</button>
            <button type="button" className="crmsq3-btn" onClick={submitCampaign} disabled={bulkBusy || !campaignId}>{bulkBusy ? 'Enrolling\u2026' : `Enrol ${Array.from(selectedIds).filter(cid => latestSeq(seqData, cid)).length}`}</button>
          </>}>
          <p className="crmsq3-modal-s">Contacts without a sequence are skipped. Enrolling also approves the sequence.</p>
          <span className="crmsq3-modal-lbl">Campaign</span>
          <select className="crmsq3-modal-sel" value={campaignId} onChange={e => setCampaignId(e.target.value)}>
            <option value="">Choose a campaign</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {campaigns.length === 0 && <p className="crmsq3-modal-note">No campaigns in this workspace yet.</p>}
        </CrmcModal>
      )}

      {bulkModal === 'delete' && (
        <CrmcModal title={`Delete ${selectedIds.size} contact${selectedIds.size === 1 ? '' : 's'}?`} busy={bulkBusy} onClose={() => setBulkModal(null)}
          footer={<>
            <button type="button" className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setBulkModal(null)} disabled={bulkBusy}>Cancel</button>
            <button type="button" className="crmsq3-btn crmsq3-btn--danger" onClick={submitDelete} disabled={bulkBusy || deleteText.trim() !== String(selectedIds.size)}>{bulkBusy ? 'Deleting\u2026' : `Delete ${selectedIds.size}`}</button>
          </>}>
          <p className="crmsq3-modal-s">Their sequences, scores, batch places and campaign enrolments go with them. This can't be undone.</p>
          <span className="crmsq3-modal-lbl">Type {selectedIds.size} to confirm</span>
          <input className="crmsq3-modal-inp" value={deleteText} onChange={e => setDeleteText(e.target.value)} autoFocus />
        </CrmcModal>
      )}

      {enrichOpen && (
        <BulkRunModal
          title="Enrich contacts"
          verb="Enrich"
          skipNote="skipped, enriched in the last 30 days"
          forceLabel="Re-enrich all selected for fresh data"
          items={buildEnrichItems()}
          concurrency={3}
          runOne={runEnrichOne}
          onClose={(ran) => { setEnrichOpen(false); if (ran) refreshList(); }}
        />
      )}

      {error && <div className="crmc-error">{error}</div>}

      <div className="crmc-body">
        {showFilters && (
          <div className="crmc-filterpanel">
            <FilterRail
              schema={CONTACT_SCHEMA}
              value={filterTree}
              onChange={(t) => { setFilterTree(t); setPage(0); setActiveViewId(null); }}
              matchCount={filterActive ? sfTotal : undefined}
              loading={sfLoading}
              savedViews={savedViews}
              onSaveView={saveCurrentView}
              onApplyView={(v) => { setFilterTree(v.tree); setPage(0); setActiveViewId(v.id); }}
              onDeleteView={deleteView}
              targets={targets}
              distincts={distincts}
            />
          </div>
        )}
        <div className="crmc-tablecol">
          <CrmTable
            columns={visibleKeys.map(k => ALL_CONTACT_COLUMNS.find(c => c.key === k)).filter(Boolean)}
            rows={pageRows}
            loading={filterActive ? sfLoading : loading}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleAll={toggleAll}
            allTypes={contactTypes}
            onTypeToggle={toggleAssign}
            onCellSave={saveCell}
            sort={sort}
            onSort={onSort}
            onContactClick={(row) => navigate(`/clients/${clientId}/crm/contacts/${row.id}`)}
          />
        </div>
      </div>

      <div className="crmc-footer">
        <div className="crmc-pagesize">
          <span>Rows per page</span>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="crmc-pager">
          <button disabled={safePage <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</button>
          <span className="crmc-pageno">{safePage + 1} of {totalPages}</span>
          <button disabled={safePage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Next</button>
        </div>
        <div className="crmc-rangeinfo">
          {totalCount === 0 ? '0' : `${safePage * pageSize + 1}\u2013${Math.min(totalCount, safePage * pageSize + pageRows.length)}`} of {totalCount}
        </div>
      </div>
    </div>
  );
}

// ---- Type manager: create types with name + colour, delete existing ----
const TYPE_COLOURS = ['#7C5CFC', '#00A878', '#2E8FFF', '#F5A623', '#F43F5E', '#06B6D4', '#EC4899', '#64748B', '#FF7A59', '#8B5CF6'];

function TypeManager({ types, onCreate, onDelete, onClose }) {
  const [name, setName] = useState('');
  const [colour, setColour] = useState(TYPE_COLOURS[0]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    await onCreate(name.trim(), colour);
    setName(''); setColour(TYPE_COLOURS[0]); setBusy(false);
  };

  return (
    <div className="crmc-colpanel crmc-typemgr">
      <div className="crmc-colpanel-head">
        <span>Contact types</span>
        <button type="button" className="crmc-colpanel-reset" onClick={onClose}>Close</button>
      </div>
      <div className="crmc-typemgr-body">
        <div className="crmc-typemgr-form">
          <input
            className="crmc-typemgr-input"
            placeholder="New type name (e.g. Decision maker)"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          />
          <div className="crmc-typemgr-swatches">
            {TYPE_COLOURS.map(c => (
              <button
                key={c}
                type="button"
                className={'crmc-swatch' + (colour === c ? ' crmc-swatch--on' : '')}
                style={{ background: c }}
                onClick={() => setColour(c)}
                title={c}
              />
            ))}
          </div>
          <button type="button" className="crmc-colbtn crmc-colbtn--apply" disabled={!name.trim() || busy} onClick={submit}>Add type</button>
        </div>
        <div className="crmc-typemgr-list">
          {types.length === 0 && <div className="crmc-colpanel-allon">No types yet. Create one above.</div>}
          {types.map(t => (
            <div key={t.id} className="crmc-typemgr-item">
              <span className="crmc-typechip" style={{ background: t.colour }}>{t.name}</span>
              <button type="button" className="crmc-colremove" title="Delete type" onClick={() => onDelete(t.id)}>×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Shared modal shell for bulk actions (reuses crmsq3 modal styles) ----
function CrmcModal({ title, onClose, busy, children, footer }) {
  return (
    <div className="crmsq3-ov" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="crmsq3-modal">
        <div className="crmsq3-modal-h">
          <h3>{title}</h3>
          <button type="button" className="crmsq3-modal-x" aria-label="Close" onClick={() => { if (!busy) onClose(); }}>{'\u00D7'}</button>
        </div>
        <div className="crmsq3-modal-b">{children}</div>
        <div className="crmsq3-modal-f">{footer}</div>
      </div>
    </div>
  );
}
